/**
 * ReportPreviewDialog Component
 *
 * A dialog component that displays a preview of platform analytics reports
 * and allows users to download the report as a PDF.
 *
 * Usage Example:
 * ```tsx
 * import { ReportPreviewDialog } from '@/components/admin/ReportPreviewDialog'
 * import { useState } from 'react'
 * import { useQuery } from '@tanstack/react-query'
 * import { adminService } from '@/lib/api/services/admin.service'
 *
 * function MyComponent() {
 *   const [previewOpen, setPreviewOpen] = useState(false)
 *   const startDate = '2024-01-01'
 *   const endDate = '2024-01-31'
 *
 *   const { data: analytics } = useQuery({
 *     queryKey: ['platform-analytics', startDate, endDate],
 *     queryFn: () => adminService.getPlatformAnalytics(startDate, endDate),
 *   })
 *
 *   return (
 *     <>
 *       <button onClick={() => setPreviewOpen(true)}>
 *         Preview Report
 *       </button>
 *
 *       <ReportPreviewDialog
 *         open={previewOpen}
 *         onOpenChange={setPreviewOpen}
 *         analytics={analytics || null}
 *         startDate={startDate}
 *         endDate={endDate}
 *       />
 *     </>
 *   )
 * }
 * ```
 *
 * Features:
 * - Displays key metrics (new geniuses, new clients, messages, connection rate)
 * - Shows summary statistics and top active geniuses
 * - Generates high-quality PDF with proper pagination
 * - Loading state during PDF generation
 * - Toast notifications for success/error feedback
 * - Responsive design with proper styling
 *
 * Dependencies:
 * - html2canvas: Converts HTML content to canvas for PDF generation
 * - jspdf: Creates PDF from canvas data
 */

"use client"

import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Download, Loader2, Users, Building2, MessageSquare, TrendingUp } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { PlatformAnalytics } from '@/lib/api/services/admin.service'
import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'

interface ReportPreviewDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  analytics: PlatformAnalytics | null
  startDate?: string
  endDate?: string
}

// Metric card for the preview
function MetricCard({
  icon: Icon,
  value,
  label,
  iconBgColor = 'bg-[rgba(70,105,237,0.10)]',
  iconColor = 'text-[#4669ed]'
}: {
  icon: React.ComponentType<{ className?: string }>
  value: string | number
  label: string
  iconBgColor?: string
  iconColor?: string
}) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 flex flex-col items-center justify-center min-h-[140px]">
      <div className={cn("w-12 h-12 rounded-full flex items-center justify-center mb-2", iconBgColor)}>
        <Icon className={cn("w-5 h-5", iconColor)} />
      </div>
      <div className="text-3xl font-semibold text-slate-900 tracking-tight">
        {value}
      </div>
      <div className="text-xs text-slate-500 mt-1.5 text-center">
        {label}
      </div>
    </div>
  )
}

export function ReportPreviewDialog({
  open,
  onOpenChange,
  analytics,
  startDate,
  endDate
}: ReportPreviewDialogProps) {
  const [isGenerating, setIsGenerating] = useState(false)
  const [toastMessage, setToastMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null)

  const showToast = (type: 'success' | 'error', text: string) => {
    setToastMessage({ type, text })
    setTimeout(() => setToastMessage(null), 3000)
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  const formatDateRange = () => {
    if (!analytics) return 'N/A'

    const start = analytics.periodStart || startDate
    const end = analytics.periodEnd || endDate

    if (!start && !end) return 'All Time'
    if (!start) return `Until ${formatDate(end)}`
    if (!end) return `From ${formatDate(start)}`

    return `${formatDate(start)} - ${formatDate(end)}`
  }

  const handleDownloadPDF = async () => {
    if (!analytics) return

    setIsGenerating(true)

    try {
      // Get the report content element
      const element = document.getElementById('report-preview-content')
      if (!element) {
        throw new Error('Report content not found')
      }

      // Generate canvas from the HTML element
      const canvas = await html2canvas(element, {
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff'
      } as Parameters<typeof html2canvas>[1])

      // Calculate PDF dimensions
      const imgWidth = 210 // A4 width in mm
      const pageHeight = 297 // A4 height in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width
      let heightLeft = imgHeight
      let position = 0

      // Create PDF
      const pdf = new jsPDF('p', 'mm', 'a4')
      const imgData = canvas.toDataURL('image/png')

      // Add first page
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight)
      heightLeft -= pageHeight

      // Add additional pages if content is longer than one page
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight
        pdf.addPage()
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight)
        heightLeft -= pageHeight
      }

      // Generate filename with date
      const date = new Date().toISOString().split('T')[0]
      const filename = `platform-analytics-report-${date}.pdf`

      // Save the PDF
      pdf.save(filename)

      showToast('success', 'PDF downloaded successfully')
    } catch (error) {
      console.error('Error generating PDF:', error)
      showToast('error', 'Failed to generate PDF')
    } finally {
      setIsGenerating(false)
    }
  }

  if (!analytics) {
    return null
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Analytics Report Preview</DialogTitle>
            <DialogDescription>
              Review the report before downloading as PDF
            </DialogDescription>
          </DialogHeader>

          {/* Report Content */}
          <div id="report-preview-content" className="space-y-6 p-6 bg-white">
            {/* Header */}
            <div className="text-center border-b border-slate-200 pb-6">
              <h1 className="text-3xl font-bold text-slate-900 mb-2">
                Platform Analytics Report
              </h1>
              <p className="text-sm text-slate-600">
                {formatDateRange()}
              </p>
            </div>

            {/* Key Metrics */}
            <div>
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Key Metrics</h2>
              <div className="grid grid-cols-2 gap-4">
                <MetricCard
                  icon={Users}
                  value={analytics.newContractors || 0}
                  label="New Geniuses"
                  iconBgColor="bg-blue-100"
                  iconColor="text-blue-600"
                />
                <MetricCard
                  icon={Building2}
                  value={analytics.newBusinesses || 0}
                  label="New Clients"
                  iconBgColor="bg-purple-100"
                  iconColor="text-purple-600"
                />
                <MetricCard
                  icon={MessageSquare}
                  value={analytics.totalMessagesSent || 0}
                  label="Messages Sent"
                  iconBgColor="bg-emerald-100"
                  iconColor="text-emerald-600"
                />
                <MetricCard
                  icon={TrendingUp}
                  value={`${analytics.connectionRate || 0}%`}
                  label="Connection Rate"
                  iconBgColor="bg-amber-100"
                  iconColor="text-amber-600"
                />
              </div>
            </div>

            {/* Summary Section */}
            <div className="bg-slate-50 rounded-xl p-4">
              <h2 className="text-lg font-semibold text-slate-900 mb-3">Summary</h2>
              <div className="space-y-2 text-sm text-slate-700">
                <div className="flex justify-between">
                  <span className="font-medium">Total Geniuses:</span>
                  <span>{analytics.totalContractors || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Total Clients:</span>
                  <span>{analytics.totalBusinesses || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Active Clients:</span>
                  <span>{analytics.totalActiveClients || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Clients Who Messaged:</span>
                  <span>{analytics.totalClientsWhoMessaged || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Message Ratio (B:C):</span>
                  <span>{analytics.businessToContractorRatio || 0}</span>
                </div>
              </div>
            </div>

            {/* Top Active Geniuses */}
            {analytics.messagesPerGenius && analytics.messagesPerGenius.length > 0 && (
              <div>
                <h2 className="text-lg font-semibold text-slate-900 mb-3">Top Active Geniuses</h2>
                <div className="space-y-2">
                  {analytics.messagesPerGenius.slice(0, 10).map((genius, index) => (
                    <div
                      key={genius.geniusId}
                      className="flex items-center justify-between bg-slate-50 rounded-lg p-3"
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-medium text-slate-500 w-6">
                          {index + 1}.
                        </span>
                        <span className="text-sm text-slate-900 font-medium">
                          {genius.displayName}
                        </span>
                      </div>
                      <span className="text-sm text-slate-600">
                        {genius.messageCount} messages
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Footer */}
            <div className="text-center border-t border-slate-200 pt-4 text-xs text-slate-500">
              Generated on {new Date().toLocaleString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </div>
          </div>

          {/* Download Button */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isGenerating}
              className="rounded-xl"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDownloadPDF}
              disabled={isGenerating}
              className="rounded-xl bg-[#4669ed] hover:bg-[#3a5ad9] text-white"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Generating PDF...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Download PDF
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Toast Notification */}
      {toastMessage && (
        <div className={cn(
          "fixed bottom-6 right-6 px-5 py-3 rounded-xl shadow-lg transition-all z-50 font-medium",
          toastMessage.type === 'success'
            ? "bg-emerald-500 text-white"
            : "bg-red-500 text-white"
        )}>
          {toastMessage.text}
        </div>
      )}
    </>
  )
}
